import { v4 as uuid } from 'uuid'
import { clearConfirm, clearPrompt } from '~/store/ui'
import { resolveAction } from './confirmActions'

const logger = console.scope('confirm')

/**
 * Confirmation middleware - handles confirm dialogs with serializable action descriptors
 *
 * Flow:
 * 1. Component dispatches requestConfirm with action descriptor
 * 2. Middleware checks "don't ask again" settings, auto-executes if disabled
 * 3. Otherwise, passes to reducer to show dialog
 * 4. When user confirms, middleware resolves descriptor and dispatches action
 */
const confirmMiddleware = store => next => action => {
	// Handle requestConfirm action
	if (action.type === 'ui/requestConfirm') {
		const {
			title,
			message,
			action: actionDescriptor,
			noAction,
			yesLabel,
			noLabel,
			confirmType,
			destructive,
		} = action.payload

		if (!title || !message) {
			logger.error('Invalid payload - missing title or message')
			return // Don't pass invalid action to reducer
		}

		// Check if this confirmation should be skipped based on settings
		if (confirmType) {
			const settings = store.getState().settings

			// Check if user has chosen to skip this confirmation type
			if (settings[confirmType] === false) {
				// Execute the action directly without showing dialog
				if (actionDescriptor) {
					const resolvedAction = resolveAction(actionDescriptor)
					if (resolvedAction) {
						store.dispatch(resolvedAction)
					}
				}
				return // Don't pass to reducer, no dialog shown
			}
		}

		// Generate a unique ID for this confirmation
		const confirmId = uuid()

		// Pass serializable action to reducer
		const serializableAction = {
			...action,
			payload: {
				id: confirmId,
				title,
				message,
				action: actionDescriptor, // Already serializable descriptor
				noAction,
				yesLabel,
				noLabel,
				confirmType,
				destructive,
			},
		}

		return next(serializableAction)
	}

	// Handle confirmAction - user clicked Yes/No
	if (action.type === 'ui/confirmAction') {
		const { confirm } = store.getState().ui
		if (!confirm || !confirm.id) {
			logger.warn('No pending confirmation to act on')
			return next(action)
		}

		// Clear the confirmation dialog
		store.dispatch(clearConfirm())

		// Execute the action if user confirmed
		const { yes } = action.payload || {}
		const descriptor = yes ? confirm.action : confirm.noAction
		if (descriptor) {
			const resolvedAction = resolveAction(descriptor)
			if (resolvedAction) {
				store.dispatch(resolvedAction)
			} else {
				logger.warn('Could not resolve action:', descriptor)
			}
		}

		return next(action)
	}

	// Handle requestPrompt action - show prompt dialog with input
	if (action.type === 'ui/requestPrompt') {
		const { title, message, inputValue, inputPlaceholder, action: actionDescriptor, destructive } = action.payload

		if (!title) {
			logger.error('Invalid prompt payload - missing title')
			return
		}

		// Generate a unique ID for this prompt
		const promptId = uuid()

		// Pass serializable action to reducer
		const serializableAction = {
			...action,
			payload: {
				id: promptId,
				title,
				message,
				inputValue: inputValue || '',
				inputPlaceholder,
				action: actionDescriptor,
				destructive,
			},
		}

		return next(serializableAction)
	}

	// Handle promptAction - user clicked Confirm/Cancel in prompt dialog
	if (action.type === 'ui/promptAction') {
		const { prompt } = store.getState().ui
		if (!prompt || !prompt.id) {
			logger.warn('No pending prompt to act on')
			return next(action)
		}

		// Get the current input value before clearing
		const { inputValue } = prompt

		// Clear the prompt dialog
		store.dispatch(clearPrompt())

		// Execute the action if user confirmed
		const { confirm } = action.payload || {}
		if (confirm && prompt.action) {
			// Merge inputValue into the action params
			const actionWithInput = {
				...prompt.action,
				params: {
					...prompt.action.params,
					inputValue,
				},
			}
			const resolvedAction = resolveAction(actionWithInput)
			if (resolvedAction) {
				store.dispatch(resolvedAction)
			} else {
				logger.warn('Could not resolve action:', actionWithInput)
			}
		}

		return next(action)
	}

	return next(action)
}

export default confirmMiddleware
